import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { inputClass, labelClass } from '../../../../../components/ui/Field';
import { EMPTY_KIT_COMPONENT } from '../../../../../features/catalog/catalogConstants';
import { productOptionLabel } from '../../../../../features/catalog/catalogUtils';
import { KIT_MODE_LABELS } from '../../../../../utils/inventoryLabels';

/**
 * What a kit is made of: its mode, and one row per component with how many of
 * it go into each kit.
 *
 * A component is offered once — the server refuses a repeated one — and never a
 * kit: there is no kit inside a kit.
 *
 * @param {object} props
 * @param {string} props.kitMode
 * @param {Array<{productId: number|null, quantity: number|string}>} props.components
 * @param {(patch: object) => void} props.onChange - Patch of `kit_mode` or `components`.
 * @param {Array<object>} props.candidates - The client's products that may go
 *   into a kit: its own catalog, minus kits and minus the product being edited.
 * @param {boolean} props.loading - The candidates are on their way.
 * @param {boolean} [props.disabled=false]
 */
export const KitComponentsEditor = ({
  kitMode,
  components,
  onChange,
  candidates,
  loading,
  disabled = false,
}) => {
  const setComponent = (index, patch) =>
    onChange({
      components: components.map((component, i) =>
        i === index ? { ...component, ...patch } : component
      ),
    });

  const takenBy = (index) =>
    components.filter((_, i) => i !== index).map((component) => component.productId);

  return (
    <div
      data-testid="kit-editor"
      className="rounded-xl border border-border bg-ninja-gray/40 dark:bg-[#111] p-4 space-y-4"
    >
      <div>
        <label htmlFor="product-kit-mode" className={labelClass}>
          Modo del kit
        </label>
        <select
          id="product-kit-mode"
          data-testid="product-kit-mode"
          value={kitMode}
          onChange={(event) => onChange({ kit_mode: event.target.value })}
          disabled={disabled}
          className={inputClass}
        >
          {Object.entries(KIT_MODE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-ninja-dark dark:text-white">
          Componentes
          <span className="ml-2 text-xs font-normal text-text-muted">SKU y cantidad por kit</span>
        </p>

        {components.map((component, index) => {
          const taken = takenBy(index);
          return (
            <div key={index} data-testid="kit-component" className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <FilterableSelect
                  variant="form"
                  testId={`kit-component-product-${index}`}
                  value={component.productId}
                  onChange={(productId) => setComponent(index, { productId })}
                  options={candidates
                    .filter((candidate) => !taken.includes(candidate.id))
                    .map((candidate) => ({
                      value: candidate.id,
                      label: productOptionLabel(candidate),
                    }))}
                  placeholder={loading ? 'Cargando productos...' : 'Seleccionar SKU...'}
                  searchPlaceholder="Buscar por SKU o nombre..."
                  disabled={disabled}
                />
              </div>
              <input
                type="number"
                min="0"
                step="any"
                aria-label={`Cantidad del componente ${index + 1}`}
                data-testid={`kit-component-quantity-${index}`}
                value={component.quantity}
                onChange={(event) => setComponent(index, { quantity: event.target.value })}
                disabled={disabled}
                className={`${inputClass} !w-24`}
              />
              <Button
                variant="subtle"
                iconOnly
                icon={X}
                aria-label={`Quitar el componente ${index + 1}`}
                title="Quitar componente"
                className="hover:text-danger"
                // A kit with no rows has no way back to one: the last stays.
                disabled={disabled || components.length === 1}
                onClick={() => onChange({ components: components.filter((_, i) => i !== index) })}
              />
            </div>
          );
        })}

        <Button
          variant="ghost"
          size="sm"
          icon={Plus}
          disabled={disabled}
          onClick={() => onChange({ components: [...components, { ...EMPTY_KIT_COMPONENT }] })}
          data-testid="kit-component-add"
        >
          Agregar componente
        </Button>
      </div>

      <p className="text-xs text-text-muted">
        Solo productos del mismo cliente, y ningún kit dentro de otro kit. Acá se declara de qué
        está hecho; cómo se reserva y se pickea un kit llega con el armado de kits.
      </p>
    </div>
  );
};
